import React from 'react'
import { Box, Typography } from '@material-ui/core'
import { alpha, useTheme } from '@material-ui/core/styles'
import styled from '@emotion/styled'
import ScienceIcon from '@mui/icons-material/Science'
import LocalHospitalIcon from '@mui/icons-material/LocalHospital'
import ViewListIcon from '@mui/icons-material/ViewList'
import BiotechIcon from '@mui/icons-material/Biotech'
import FunctionChips from './FunctionChips'
import TruncatedChip from './TruncatedChip'
import useChatbotStore from 'src/components/chatbot/stores/chatbotStore'

const UserMessage = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing(2)};
  padding: ${({ theme }) => theme.spacing(1.5, 2)};
  border-radius: 12px;
  max-width: 80%;
  word-wrap: break-word;
  background-color: ${({ theme }) => theme.palette.primary.main};
  /* contrastText is white on light olive and near-black on dark-mode olive (both >= 4.5:1). */
  color: ${({ theme }) => theme.palette.primary.contrastText};
  align-self: flex-end;
`

const ChipsContainer = styled(Box)`
  display: flex;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing(0.5)};
  margin-top: ${({ theme }) => theme.spacing(1)};
  padding-top: ${({ theme }) => theme.spacing(1)};
  border-top: 1px solid ${({ theme }) => alpha(theme.palette.primary.contrastText, 0.2)};
`

const StyledTruncatedChip = styled(TruncatedChip)`
  font-size: 0.75rem;
  height: 24px;
  background-color: ${({ theme }) => alpha(theme.palette.primary.contrastText, 0.15)};
  color: ${({ theme }) => theme.palette.primary.contrastText};
  border: 1px solid ${({ theme }) => alpha(theme.palette.primary.contrastText, 0.3)};

  & .MuiChip-icon {
    color: ${({ theme }) => theme.palette.primary.contrastText};
    font-size: 0.75rem;
  }
`

const ChipSection = styled(Box)`
  display: flex;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing(0.5)};
  margin-bottom: ${({ theme }) => theme.spacing(0.5)};
`

const ChipSectionLabel = styled(Typography)`
  font-size: 0.75rem;
  color: ${({ theme }) => theme.palette.primary.contrastText};
  margin-bottom: ${({ theme }) => theme.spacing(0.5)};
  font-weight: 600;
`

const UserMessageWithChips = ({ userMessage }) => {
  const theme = useTheme()
  const { content, selectedChips } = userMessage
  const onBubble = theme.palette.primary.contrastText
  // Chips sit on the primary bubble, so they take its contrast colour.
  const bubbleChipSx = {
    fontSize: '0.75rem',
    height: 24,
    backgroundColor: alpha(onBubble, 0.15),
    color: onBubble,
    border: `1px solid ${alpha(onBubble, 0.3)}`,
    '& .MuiChip-icon': {
      color: onBubble,
      fontSize: '0.75rem',
    },
  }

  const hasAnyChips =
    selectedChips &&
    ((selectedChips.hpoTerms && selectedChips.hpoTerms.length > 0) ||
      (selectedChips.diseases && selectedChips.diseases.length > 0) ||
      (selectedChips.samples && selectedChips.samples.length > 0) ||
      (selectedChips.panels && selectedChips.panels.length > 0))

  const getIcon = (type) => {
    switch (type) {
      case 'hpo':
        return <ScienceIcon style={{ fontSize: '0.75rem' }} />
      case 'disease':
        return <LocalHospitalIcon style={{ fontSize: '0.75rem' }} />
      case 'sample':
        return <ViewListIcon style={{ fontSize: '0.75rem' }} />
      case 'panel':
        return <BiotechIcon style={{ fontSize: '0.75rem' }} />
      default:
        return null
    }
  }

  return (
    <UserMessage>
      <Typography variant="body2">{content}</Typography>

      {hasAnyChips && (
        <ChipsContainer>
          {selectedChips.hpoTerms && selectedChips.hpoTerms.length > 0 && (
            <Box width="100%">
              <ChipSectionLabel variant="caption">HPO Terms:</ChipSectionLabel>
              <ChipSection>
                {selectedChips.hpoTerms.map((term, index) => (
                  <StyledTruncatedChip
                    key={`hpo-${index}`}
                    size="small"
                    label={term.name}
                    icon={getIcon('hpo')}
                    variant="outlined"
                    maxWidth={180}
                    chipProps={{ sx: bubbleChipSx }}
                  />
                ))}
              </ChipSection>
            </Box>
          )}

          {selectedChips.diseases && selectedChips.diseases.length > 0 && (
            <Box width="100%">
              <ChipSectionLabel variant="caption">Diseases:</ChipSectionLabel>
              <ChipSection>
                {selectedChips.diseases.map((disease, index) => (
                  <StyledTruncatedChip
                    key={`disease-${index}`}
                    size="small"
                    label={disease.disease}
                    icon={getIcon('disease')}
                    variant="outlined"
                    maxWidth={180}
                    chipProps={{ sx: bubbleChipSx }}
                  />
                ))}
              </ChipSection>
            </Box>
          )}

          {selectedChips.samples && selectedChips.samples.length > 0 && (
            <Box width="100%">
              <ChipSectionLabel variant="caption">Samples:</ChipSectionLabel>
              <ChipSection>
                {selectedChips.samples.map((sample, index) => (
                  <StyledTruncatedChip
                    key={`sample-${index}`}
                    size="small"
                    label={sample.name}
                    icon={getIcon('sample')}
                    variant="outlined"
                    maxWidth={180}
                    chipProps={{ sx: bubbleChipSx }}
                  />
                ))}
              </ChipSection>
            </Box>
          )}

          {selectedChips.panels && selectedChips.panels.length > 0 && (
            <Box width="100%">
              <ChipSectionLabel variant="caption">Panels:</ChipSectionLabel>
              <ChipSection>
                {selectedChips.panels.map((panel, index) => (
                  <StyledTruncatedChip
                    key={`panel-${index}`}
                    size="small"
                    label={panel.name}
                    icon={getIcon('panel')}
                    variant="outlined"
                    maxWidth={180}
                    chipProps={{ sx: bubbleChipSx }}
                  />
                ))}
              </ChipSection>
            </Box>
          )}
        </ChipsContainer>
      )}
    </UserMessage>
  )
}

export default UserMessageWithChips
